/**
 * Zod validation schemas for the booking pipeline.
 *
 * Every payload sent to the process-booking edge function must be
 * validated through these schemas first. This ensures:
 *   - No undefined, null, or malformed fields reach the edge function
 *   - Runtime type enforcement (UUIDs, enums, numeric ranges)
 *   - Business rules (adults >= 1, demographics match total)
 *
 * The inferred types re-export the centralized interfaces from
 * @mondivago/types so there's a single source of truth.
 */

import { z } from 'zod';

// ─── UUID ────────────────────────────────────────────────────────────

const uuidSchema = z.string().uuid('Invalid UUID format');

// ─── Demographics ────────────────────────────────────────────────────

export const demographicsSchema = z.object({
  adult: z.number().int().min(1, 'At least 1 adult is required'),
  child: z.number().int().min(0).default(0),
  infant: z.number().int().min(0).default(0),
  senior: z.number().int().min(0).default(0),
});

// ─── Addon selection ─────────────────────────────────────────────────

export const addonItemSchema = z.object({
  addon_id: uuidSchema,
  quantity: z.number().int().min(1, 'Addon quantity must be at least 1'),
});

export const addonsArraySchema = z.array(addonItemSchema).default([]);

// ─── Promo code (optional inner data) ────────────────────────────────

export const promoCodeAppliedSchema = z.object({
  id: uuidSchema,
  code: z.string().min(1),
  discount_type: z.enum(['percentage', 'fixed']),
  discount_value: z.number().positive(),
  discount_amount: z.number().min(0),
  basePrice: z.number().min(0),
  finalPrice: z.number().min(0),
});

// ─── Booking Data — the payload sent INSIDE bookingData ──────────────

export const bookingDataSchema = z.object({
  tour_id: uuidSchema,
  availability_id: uuidSchema,
  supplier_id: uuidSchema,
  number_of_people: z.number().int().min(1, 'At least 1 participant'),
  traveler_demographics: demographicsSchema,
  total_price: z.number().min(0),
  platform_fee: z.number().min(0).default(0),
  special_requests: z.string().max(2000).nullable().default(null),
  financial_status: z.enum(['pending', 'captured', 'authorized']).default('pending'),
  addons: addonsArraySchema,
  channel: z.enum(['direct', 'partner', 'supplier_manual']).default('direct'),
  is_manual: z.boolean().default(false),
  partner_id: uuidSchema.nullable().default(null),
  partner_agent_id: uuidSchema.nullable().default(null),
  partner_commission: z.number().min(0).default(0),
  promo_code_id: uuidSchema.nullable().default(null),
  promo_discount: z.number().min(0).default(0),
  supplier_notes: z.string().max(5000).nullable().default(null),
  addons_total: z.number().min(0).optional(),
});

// ─── Guest Checkout — the full payload to process-booking function ────

export const guestCheckoutSchema = z.object({
  travelerEmail: z.string().email('Valid traveler email is required'),
  travelerName: z.string().min(1, 'Traveler name is required').max(200),
  travelerPhone: z.string().max(30).nullable().default(null),
  bookingData: bookingDataSchema,
  targetStartTime: z.string().min(1, 'Start time is required'),
  idempotency_key: z.string().uuid('Idempotency key must be a UUID'),
  skipPayment: z.boolean().default(false),
  skipCustomerSetup: z.boolean().default(false),
  skipBooking: z.boolean().default(false),
  existingBookingId: uuidSchema.optional(),
  type: z.enum(['booking', 'tip']).default('booking'),
  metadata: z.record(z.unknown()).default({}),
});

// ─── Manual Booking (supplier) — transformed to guest-checkout shape ─

export const manualBookingSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email(),
  phone: z.string().max(30).default(''),
  tourId: uuidSchema,
  scheduleId: uuidSchema,
  targetStartTime: z.string().nullable().default(null),
  supplierId: uuidSchema,
  demographics: demographicsSchema,
  totalPrice: z.number().min(0),
  financialStatus: z.enum(['pending', 'captured', 'authorized']).default('captured'),
  addons: addonsArraySchema,
  specialRequests: z.string().max(2000).nullable().default(null),
  supplierNotes: z.string().max(5000).nullable().default(null),
});

// ─── Helper: extract clean error messages from Zod ──────────────────

export function formatZodError(error: z.ZodError): string {
  const messages = error.errors.map((e) => {
    const path = e.path.length > 0 ? `${e.path.join('.')}: ` : '';
    return `${path}${e.message}`;
  });
  return messages.join('; ');
}
